import {Button} from '@dropbox/dig-components/buttons';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {
  ApiError,
  type ProductRoadmapEmbedConfigResponse,
  ProductRoadmapService,
  type Workstream,
} from 'client';
import {t} from 'i18next';
import {useState} from 'react';
import {getService} from 'utilities';
import {getConfluenceEmbedConfig} from 'views/productRoadmap/confluenceEmbed';
import {productRoadmapQueryKey} from 'views/productRoadmap/hooks';
import {invalidateNavTrees} from 'views/workstreams/page/useNavTree';
import {logWorkstreamsInteraction} from 'views/workstreams/page/workstreamsAnalytics';

import {WorkstreamModalFieldError} from './WorkstreamModalField';

type RoadmapWorkstream = Pick<Workstream, 'id'>;

// The legacy link popover uses "Roadmap" as the default title.
const ROADMAP_TITLE = 'Roadmap';

// Any web link, as the server accepts (api/endpoints/product_roadmap.py).
export const isWorkstreamProductRoadmapUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
};

export const supportsEmbeddedPreview = (value: string): boolean => {
  if (!isWorkstreamProductRoadmapUrl(value)) {
    return false;
  }
  const url = new URL(value.trim());
  if (url.protocol !== 'https:') {
    return false;
  }
  if (url.host === 'docs.google.com') {
    return /^\/(document|presentation)\/d\/[A-Za-z0-9_-]+(?:\/.*)?$/.test(url.pathname);
  }
  if (getConfluenceEmbedConfig(value)) {
    return true;
  }
  return (
    url.host === 'www.dropbox.com' && /^\/scl\/fi\/[A-Za-z0-9]+\/[^/]+\.paper$/.test(url.pathname)
  );
};

export const roadmapSaveErrorMessage = (error: unknown) =>
  error instanceof ApiError && error.status < 500 && typeof error.body?.detail === 'string'
    ? error.body.detail
    : t('unable_to_save');

/**
 * Paste a roadmap link and save: adds one, or relinks `roadmap` when set. A hint
 * shows under the field; it turns into the error once Add has been tried with
 * something that isn't a web link, and stays live after that.
 */
export const RoadmapLinkForm = ({
  workstream,
  roadmap,
  onDone,
}: {
  workstream: RoadmapWorkstream;
  roadmap: ProductRoadmapEmbedConfigResponse | null;
  onDone: () => void;
}) => {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(roadmap?.open_url ?? '');
  const [attempted, setAttempted] = useState(false);
  const {mutate, isPending, error, reset} = useMutation({
    mutationFn: (link: string) => {
      const document = {title: roadmap?.title ?? ROADMAP_TITLE, url: link};
      return roadmap?.id
        ? getService(ProductRoadmapService).editProductRoadmapApiV1ProductRoadmapRoadmapIdPatch(
            roadmap.id,
            document
          )
        : getService(ProductRoadmapService).createProductRoadmapApiV1ProductRoadmapPost({
            workstream_id: workstream.id,
            ...document,
          });
    },
    retry: false,
    onSuccess: () => {
      logWorkstreamsInteraction(roadmap ? 'roadmap_edited' : 'roadmap_added', {
        workstream_id: workstream.id,
      });
      onDone();
      invalidateNavTrees();
      return queryClient.invalidateQueries({
        queryKey: productRoadmapQueryKey({workstreamId: workstream.id}),
        exact: true,
      });
    },
  });
  const isValid = isWorkstreamProductRoadmapUrl(url);
  const isInvalid = attempted && url.trim().length > 0 && !isValid;
  const message = isInvalid
    ? t('workstream_product_roadmap_invalid_link')
    : error
      ? roadmapSaveErrorMessage(error)
      : null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (isValid && !isPending) mutate(url.trim());
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="8" style={{width: 400}}>
        <Text size="small" isBold>
          {roadmap ? t('workstream_product_roadmap_edit') : t('workstream_product_roadmap_add')}
        </Text>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Box style={{flex: 1, minWidth: 0}}>
            <TextInput
              aria-label={t('workstream_product_roadmap_link')}
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                reset();
              }}
              placeholder="https://"
              autoFocus
              disabled={isPending}
              isInvalid={Boolean(message)}
              size="small"
            />
          </Box>
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={!url.trim() || isPending}
            isLoading={isPending}
          >
            {roadmap ? t('save') : t('add')}
          </Button>
        </Box>
        {message ? (
          <WorkstreamModalFieldError>{message}</WorkstreamModalFieldError>
        ) : (
          <Text size="small" color="faint">
            {t('workstream_product_roadmap_hint')}
          </Text>
        )}
      </Stack>
    </form>
  );
};
